import { Map, View } from 'ol';
import { Vector as VectorLayer, Group as LayerGroup } from 'ol/layer';
import { Graticule } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Stroke } from 'ol/style';
import { defaults as defaultControls, ScaleLine } from 'ol/control';
import * as olExtent from 'ol/extent';
import { fromLonLat } from 'ol/proj';
import { apply } from 'ol-mapbox-style';
import { KML } from 'ol/format';
import { KMZ } from '../shared/nhc-modules/ol-kmz';
import { PopupOverlay } from '../shared/nhc-modules/map-popups';
import { BaseMapToggle } from '../shared/nhc-modules/basemap-toggle';
import { MouseCoordinates } from '../shared/nhc-modules/mouse-coordinates';
import './style.css'; // Moved to the bottom so it can override other styles

/**
 * These variables are set in the including HTML file:
 *   text1, current, layers
 */

let mySelect = null;
let title = null;

/**
 * The list of checkboxes, which also servers as a list of layers.
 * They are in the order that the checkboxes are listed.
 * The sort field is the order that the layers are added to the map.
 */
const checks = {
  "track": {
    "sort": 11,
    "divId": "tracktoggle",
    "label": "Track",
    "labelShort": "Track",
    "checked": false,
    "layerId": "urlTrack",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "_TRACK.kmz",
    "layer": {}
  },
  "cone": {
    "sort": 8,
    "divId": "conetoggle",
    "label": "Cone",
    "labelShort": "Cone",
    "checked": true,
    "layerId": "urlCone",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "_CONE.kmz",
    "layer": {}
  },
  "ww": {
    "sort": 9,
    "divId": "wwtoggle",
    "label": "Wind Watches/Warnings",
    "labelShort": "Wind Watches/Warnings",
    "checked": true,
    "layerId": "urlWW",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "_WW.kmz",
    "layer": {}
  },
  "ssww": {
    "sort": 10,
    "divId": "sswwtoggle",
    "label": "Storm Surge Watches/Warnings",
    "labelShort": "Storm Surge Watches/Warnings",
    "checked": true,
    "layerId": "urlSSWW",
    "lineEnd": false,
    "format": "KML",
    "idStr": "_WatchWarningSS_",
    "layer": {}
  },
  "rad": {
    "sort": 7,
    "divId": "radtoggle",
    "label": "Initial Wind Radii",
    "labelShort": "Initial Wind Radii",
    "checked": true,
    "layerId": "urlRadii",
    "lineEnd": true,
    "format": "KMZ",
    "idStr": "_initialradii_",
    "layer": {}
  },
  "ts": {
    "sort": 2,
    "divId": "tstoggle",
    "label": "34kt WSP",
    "labelShort": "34kt WSP",
    "checked": false,
    "layerId": "url34wsp",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "wsp34knt120hr_5km",
    "layer": {}
  },
  "fi": {
    "sort": 3,
    "divId": "fitoggle",
    "label": "50kt WSP",
    "labelShort": "50kt WSP",
    "checked": false,
    "layerId": "url50wsp",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "wsp50knt120hr_5km",
    "layer": {}
  },
  "hu": {
    "sort": 4,
    "divId": "hutoggle",
    "label": "64kt WSP",
    "labelShort": "64kt WSP",
    "checked": false,
    "layerId": "url64wsp",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "wsp64knt120hr_5km",
    "layer": {}
  },
  "bt": {
    "sort": 1,
    "divId": "bttoggle",
    "label": "Past Track",
    "labelShort": "Past Track",
    "checked": false,
    "layerId": "urlBT",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "_best_track.kmz",
    "layer": {}
  },
  "zoom": {
    "sort": 0,
    "divId": "zoomtoggle",
    "label": "Auto-Zoom (on cone load)",
    "labelShort": "",
    "checked": true,
    "layerId": "",
    "lineEnd": true,
    "format": "KMZ",
    "idStr": "_zoom_",
    "layer": {}
  },
  "er": {
    "sort": 5,
    "divId": "ertoggle",
    "label": "Earliest Reasonable Arrival Time of TS Winds",
    "labelShort": "Earliest Reasonable Arrival Time",
    "checked": false,
    "layerId": "urlertoa",
    "lineEnd": false,
    "format": "KMZ",
    "idStr": "_earliest_reasonable_toa_",
    "layer": {}
  },
  "ml": {
    "sort": 6,
    "divId": "mltoggle",
    "label": "Most Likely Arrival Time of TS winds",
    "labelShort": "Most Likely Arrival Time",
    "checked": false,
    "layerId": "urlmltoa",
    "lineEnd": true,
    "format": "KMZ",
    "idStr": "_most_likely_toa_",
    "layer": {}
  }
};

const mapCenter = fromLonLat([-70.0, 35.0]);

/**
 * Define the scale bar control
 */
const scaleBar = new ScaleLine({
  units: "imperial",
  bar: true
});

/**
 * Define the popup overlay
 */
const popupOverlay = new PopupOverlay({
  element: document.getElementById('popup'),
  autoPan: {
    animation: {
      duration: 250,
    },
  },
  labelSource: checks,
});

/**
 * Create an ESRI basemap layer group (Light Gray)
 */
const grayLayer = new LayerGroup();
const apiKey = import.meta.env.VITE_ESRI_API_KEY;
let basemapId = "arcgis/light-gray";
let basemapURL = `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/${basemapId}?token=${apiKey}`;
apply(grayLayer, basemapURL);

/**
 * Create an ESRI basemap layer group (Imagery)
 */
const hybridLayer = new LayerGroup();
basemapId = "arcgis/imagery";
basemapURL = `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/${basemapId}?worldview=unitedStatesOfAmerica&token=${apiKey}`;
apply(hybridLayer, basemapURL);

/**
 * Create an ESRI basemap layer group (NHC Light Gray Canvas)
 */
const attrLayer = new VectorLayer({
  source: new VectorSource({
    attributions: 'Sources: Esri, TomTom, Garmin, FAO, NOAA, USGS, © OpenStreetMap contributors, and the GIS User Community',
  }),
});
const nhcLayer = new LayerGroup({
  layers: [attrLayer],
});
const glStyle = "/arcgis-map-styles/nhc-light-gray-canvas.json";
apply(nhcLayer, glStyle);

/**
 * Define the graticule layer
 */
const graticule = new Graticule({
    // the style to use for the lines, optional.
    strokeStyle: new Stroke({
      color: "rgba(0, 0, 0, 0.2)",
      width: 1,
      lineDash: [0.5, 4],
    }),
    targetSize: 60,
    showLabels: true,
    wrapX: true,
  });

/**
 * Define the base map toggle control
 */
const baseMapToggle = new BaseMapToggle({
  //startLayer: grayLayer,
  //startLayerName: 'Light Gray Canvas',
  //startLayerImage: '/images/gray.jpg',
  startLayer: nhcLayer,
  startLayerName: 'NHC Grey/Blue Canvas',
  startLayerImage: '/images/nhc-blue.jpg',
  nextLayer: hybridLayer,
  nextLayerName: 'Imagery with Labels',
  nextLayerImage: '/images/hybrid.jpg'
});

/**
 * Define the mouse coordinates control
 */
const mouseCoordinates = new MouseCoordinates({
  target: document.getElementById('container'),
});

/**
 * Create the map
 */
const map = new Map({
  controls: defaultControls().extend([
    scaleBar,
    baseMapToggle,
    mouseCoordinates
  ]),
  layers: [
    //grayLayer,
    nhcLayer,
    graticule,
  ],
  overlays: [popupOverlay],
  target: document.getElementById('map'),
  view: new View({
    center: mapCenter,
    projection: 'EPSG:3857', // EPSG:4326
    zoom: 3,
    minZoom: 2,
    maxZoom: 12,
  }),
});

/**
 * Once the DOM is loaded and the page is ready...
 */
$(function() {

  /* Build the controls for the map */
  addTitle(current);
  addSelections(current);
  addBoxes(current);

  /* Load KMZ layers using Open Layers */
  loadLayers(current);
  autoZoom();

  /**
   * Add event listeners using jQuery
   */

  // select menu event
  $("#lplist").on("change", function() {
    let previous = current;
    current = mySelect.options[mySelect.selectedIndex].value;
    addTitle(current);
    removeLayers(previous);
    loadLayers(current);
    autoZoom();
  });

  // checkbox events
  for (let [key, check] of Object.entries(checks)) {
    $("#" + check.divId).on("change", function (t) {
      check.checked = t.currentTarget.checked;
      if (key !== "zoom") {
        checks[key].layer[current].setVisible(t.currentTarget.checked);
      }
    });
  }

  // clear map button event
  $("#clear").on("click", function () {
    // Turn off visibility of all layers
    for (let [key, check] of Object.entries(checks)) {
      if (key === "zoom") continue;
      if (checks[key].layer[current]) {
        checks[key].layer[current].setVisible(false);
      }
    }
    uncheckBoxes();
  });
});


// ================================================
// Support functions
// ================================================

/**
 * Remove all layers from the map
 */
function removeLayers(previous) {
  for (let [key, check] of Object.entries(checks)) {
    if (checks[key].layer[previous]) {
      map.removeLayer(checks[key].layer[previous]);
    }
  }
}

/**
 * Check two different states to see if the layer has content
 * The jscone.csh generated files exclude layers that are not used
 * @param {string} current - Current advisory number
 * @param {object} check - The current check object
 * @returns {boolean} - Whether the layer has content
 */
function checkIsLayer(current, check) {
  let isLayer = false;
  if (layers[current][check.layerId]) {
    isLayer = (layers[current][check.layerId] !== "") ? true : false;
  }
  return isLayer;
}

/**
 * Load the layers for the current advisory.
 * @param {string} current - Current advisory number
 */
function loadLayers(current) {
  // Make an array of the keys in the checks object, sorted by the sort field
  const sortedKeys = Object.keys(checks).sort((a, b) => {
    return checks[a].sort - checks[b].sort;
  });

  // Loop through the sorted keys and add the layers to the map
  for (let key of sortedKeys) {
    let check = checks[key];
    // Zoom is not a KMZ layer
    if (key === "zoom") {
      continue;
    };

    let input = document.getElementById(check.divId);
    let label = document.querySelector("label[for='" + check.divId + "']");

    let isLoaded = (check.layer[current]) ? true : false;
    let isLayer = checkIsLayer(current, check);

    // If the layer was already displayed on the map, then we don't need
    // to reload it. We just need to re-add it to the map.
    if (isLayer && isLoaded) {
      input.disabled = false;
      label.style.color = "black";
      map.addLayer(check.layer[current]);
      // Mark the source as changed so the auto-zoom will work
      let source = check.layer[current].getSource();
      source.changed();
    }
    // If this is the first time we are displaying this layer, then we need
    // to load it from the URL and add it to the map.
    else if (isLayer && !isLoaded) {
      // Set the URL to the local file path
      let url = layers[current][check.layerId];
      // Halo-generated files use http://
      url = url.replace("http://www.nhc.noaa.gov", "");
      url = url.replace("https://www.nhc.noaa.gov", "");

      input.disabled = false;
      label.style.color = "black";

      if (check.format === "KML") {
        check.layer[current] = new VectorLayer({
          source: new VectorSource({
            url: url,
            format: new KML({
              showPointNames: false,
            })
          }),
          visible: check.checked
        });
      }
      else { // KMZ
        check.layer[current] = new VectorLayer({
          source: new VectorSource({
            url: url,
            format: new KMZ({
              showPointNames: false,
            }),
          }),
          visible: check.checked
        });
      }
      map.addLayer(check.layer[current]);
    }
    // If the layer is not available, then we disable the checkbox
    else {
      check.layer[current] = null;
      input.disabled = true;
      label.style.color = "gray";
    }
  }
}

/**
 * Auto-zoom the map to the cone graphic's extent if the "Auto-Zoom"
 * checkbox is checked.
 */
function autoZoom() {
  const autozoom = document.querySelector("#zoomtoggle").checked;
  if (autozoom) {
    // Get the cone layer
    let check = checks.cone;
    let source = check.layer[current].getSource();
    // To ensure that the source is loaded, we listen for the source
    // to change (which includes when it finishes loading)
    source.on('change', function () {
      // Then we check if the source is ready
      if (source.getState() === 'ready') {
        let coneExtent = source.getExtent();
        // Only zoom if the extent is not empty
        if (coneExtent && !olExtent.isEmpty(coneExtent)) {
          // Expand the extent by a factor of 2
          olExtent.scaleFromCenter(coneExtent, 2);
          let view = map.getView();
          let resolution = view.getResolutionForExtent(coneExtent);
          let zoom = view.getZoomForResolution(resolution);
          view.animate({
            duration: 700, // Duration in milliseconds
            center: olExtent.getCenter(coneExtent),
            zoom: zoom
          });
        }
      }
    });
  }
}

/**
 * Create content for the title div tag using data from the cone.js file
 * @param {string} current - Current advisory number
 */
function addTitle(current) {
  if (title != null) {
    document.getElementById("title").removeChild(title);
  }
  title = formatTitle(current);
  document.getElementById("title").appendChild(title);
}

/**
 * Create the title div tag using data from the cone.js file
 * @param {string} current - Current advisory number
 * @returns {string} - The title div block
 */
function formatTitle(current) {
  const layerT = document.createElement("div");
  layerT.style.fontWeight = "bold";
  layerT.style.fontSize = "12";
  layerT.appendChild(document.createTextNode(text1));
  layerT.appendChild(document.createElement("br"));
  layerT.appendChild(
    document.createTextNode(
      layers[current].storm + " " + layers[current].name
    )
  );
  layerT.appendChild(document.createElement("br"));
  layerT.appendChild(document.createTextNode(layers[current].initialtime));
  layerT.appendChild(document.createElement("br"));
  return layerT;
}

/**
 * Create the select menu using data from the cone.js file
 * @param {string} current - Current advisory number
 */
function addSelections(current) {
  // layerS is the selection div tag
  const layerS = document.createElement("div");
  layerS.style.fontWeight = "bold";
  layerS.style.fontSize = "12";
  const text = document.createTextNode("Select Advisory: ");
  layerS.appendChild(text);
  mySelect = document.createElement("select");
  mySelect.id = "lplist";
  // Create the options for the select menu
  for (let adv in layers) {
    const option = document.createElement("option");
    option.text = layers[adv].name;
    option.value = adv;
    try {
      // Standards compliant
      mySelect.add(option, null);
    } catch (ex) {
      // IE only
      mySelect.add(option);
    }
  }
  layerS.appendChild(mySelect);
  let pulldown = layerS;
  document.getElementById("selections").appendChild(pulldown);
}

/**
 * Create the checkboxes. This function is called when the map is loaded
 * and when a new advisory is selected.
 * @param {string} current - Current advisory number
 */
function addBoxes(current) {
  const layerB = document.createElement("div");
  layerB.style.fontWeight = "bold";
  layerB.style.fontSize = "12";

  for (let [key, check] of Object.entries(checks)) {
    let box = document.createElement("INPUT");
    box.setAttribute("type", "checkbox");
    box.setAttribute("name", check.divId);
    box.id = check.divId;

    // Check or uncheck the box as set in the defaults.
    // If the current layer is not available, it may be
    // checked and disabled, which is better for other 
    // advisory packages that may have the layer.
    box.checked = check.checked;

    const label = document.createElement("Label");
    label.setAttribute("for", check.divId);
    label.innerHTML = check.label;

    if (check.lineEnd) {
      layerB.appendChild(box);
      layerB.appendChild(document.createTextNode(" "));
      layerB.appendChild(label);
      layerB.appendChild(document.createElement("br"));
    }
    else {
      layerB.appendChild(box);
      layerB.appendChild(document.createTextNode(" "));
      layerB.appendChild(label);
      layerB.appendChild(document.createTextNode(" | "));
    }
  }
  document.getElementById("checkboxes").appendChild(layerB);
}

/**
 * Uncheck all checkboxes. Used by the clear button.
 */
function uncheckBoxes() {
  for (let [key, check] of Object.entries(checks)) {
    check.checked = false;
    let box = document.getElementById(check.divId);
    box.checked = false;
  }
}